// Which account (or accounts) the portfolio page is showing, and the maths that
// follows from that choice.
//
// The page used to be one view of everything, with a platform filter that only
// reached the positions table: choosing Fidelity left the headline value, the
// chart, the performance block and the breakdown describing the whole
// portfolio. The selection is a property of the PAGE now, and everything on it
// is derived from the accounts in scope.
//
// Nothing here fetches. The snapshot already carries every account's own NAV
// series and performance, so a switch between views is arithmetic, not a
// request.

import { valueChange, windowPerformance } from './timeframes';

export const ALL = 'all';

/** Stable identity for an account. `accountLabel` is not one: two IBKR
 *  connections both carry the label "Interactive Brokers". */
export const accountKey = (account) =>
    `${account.source}:${account.accountId ?? account.accountLabel}`;

/**
 * The views to offer, in the order they should appear.
 *
 * Grouped by broker and book rather than one entry per account: "IBKR (other)"
 * is however many accounts are not the index, and collapsing them is what the
 * reader actually asked for. A group with no accounts is not offered.
 */
export function accountViews(accounts = []) {
    // With no account designated as the index there is no "other" to be other
    // THAN — which is the state of a snapshot taken before accounts carried an
    // id, and of a book that simply has one IBKR account. Calling that group
    // "IBKR (other)" would name a distinction that does not exist.
    const hasIndex = accounts.some((a) => a.source === 'ibkr' && a.scope === 'index');

    const groups = [
        {
            key: 'ibkr-index',
            labelKey: 'accounts.ibkrIndex',
            accounts: accounts.filter((a) => a.source === 'ibkr' && a.scope === 'index'),
        },
        {
            key: 'ibkr-other',
            labelKey: hasIndex ? 'accounts.ibkrOther' : 'accounts.ibkr',
            accounts: accounts.filter((a) => a.source === 'ibkr' && a.scope !== 'index'),
        },
        {
            key: 'fidelity',
            labelKey: 'accounts.fidelity',
            accounts: accounts.filter((a) => a.source === 'fidelity'),
        },
        {
            // Every tracked wallet, one account each. After the brokers,
            // because no broker statement stands behind it.
            key: 'crypto',
            labelKey: 'accounts.crypto',
            accounts: accounts.filter((a) => a.source === 'crypto'),
        },
        {
            // The active private investments, at the marks typed in for them.
            key: 'private',
            labelKey: 'accounts.private',
            accounts: accounts.filter((a) => a.source === 'private'),
        },
    ].filter((g) => g.accounts.length > 0);

    // With one group there is nothing to switch between, and an All/X pair that
    // always shows the same thing is worse than no control at all.
    if (groups.length < 2) return [];
    return [{ key: ALL, labelKey: 'common.all', accounts }, ...groups];
}

/** The accounts a view covers, falling back to everything. */
export const accountsInView = (views, key) =>
    views.find((v) => v.key === key)?.accounts ?? [];

/** Does this position belong to the selected view? Matched on accountId, which
 *  is the only field that separates two accounts at the same broker. */
export function positionsInView(positions, accounts) {
    const ids = new Set(accounts.map((a) => a.accountId));
    const sources = new Set(accounts.map((a) => a.source));
    return positions.filter((p) =>
        // A position with no accountId predates the field or came from a source
        // that reports none; fall back to the broker so it is not silently
        // dropped from a view its account is part of.
        p.accountId ? ids.has(p.accountId) : sources.has(p.source));
}

const sum = (values) => values.reduce((acc, v) => acc + Number(v), 0);

/** Total value across the accounts in view, or null if any cannot say. */
export function totalValue(accounts) {
    if (accounts.length === 0) return null;
    if (accounts.some((a) => a.totalValue === null || a.totalValue === undefined)) return null;
    return sum(accounts.map((a) => a.totalValue)).toFixed(2);
}

/**
 * Adds several accounts' NAV series into one, forward-filling each.
 *
 * Mirrors `sumNavSeries` on the server, which builds the portfolio-wide series
 * the same way. It has to exist twice because a view can be an arbitrary subset
 * of accounts and only the client knows which subset is selected.
 */
export function sumNavSeries(seriesList) {
    const present = seriesList.filter((s) => s && s.length > 0);
    if (present.length === 0) return [];
    if (present.length === 1) return present[0];

    const dates = [...new Set(present.flatMap((s) => s.map((p) => p.date)))].sort();
    const cursors = present.map(() => -1);

    return dates.map((date) => {
        let total = 0;
        let cash = 0;
        let sawCash = false;
        let currency = null;
        present.forEach((points, i) => {
            while (cursors[i] + 1 < points.length && points[cursors[i] + 1].date <= date) {
                cursors[i] += 1;
            }
            const at = cursors[i] >= 0 ? points[cursors[i]] : null;
            // Before an account's first point it was worth nothing — not worth
            // its opening balance, which would invent money.
            if (!at) return;
            total += Number(at.total);
            if (at.cash !== null && at.cash !== undefined) { cash += Number(at.cash); sawCash = true; }
            currency ??= at.currency;
        });
        return {
            date,
            total: total.toFixed(2),
            cash: sawCash ? cash.toFixed(2) : null,
            securities: null,
            currency: currency ?? 'USD',
        };
    });
}

const ADDITIVE = [
    'startingValue', 'endingValue', 'netDeposits', 'netPnl',
    'markToMarket', 'realized', 'dividendsAndInterest', 'feesAndCommissions',
];

/**
 * Adds several accounts' performance into one.
 *
 * Mirrors `combinePerformance` on the server, for the same reason
 * `sumNavSeries` does. Every figure is additive except the time-weighted
 * return, which is not: combining two accounts' TWRs needs value-weighting by
 * each one's average balance, and both summing them and taking the first read
 * as a real portfolio return.
 */
export function combinePerformance(summaries) {
    const present = summaries.filter(Boolean);
    if (present.length === 0) return null;
    if (present.length === 1) return present[0];

    const field = (key) => {
        const values = present
            .map((p) => p[key])
            .filter((v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)));
        // All or nothing: a partial sum is a number that looks complete.
        if (values.length !== present.length) return null;
        return sum(values).toFixed(2);
    };

    const out = {
        fromDate: present.map((p) => p.fromDate).filter(Boolean).sort()[0] ?? null,
        toDate: present.map((p) => p.toDate).filter(Boolean).sort().at(-1) ?? null,
        currency: present.find((p) => p.currency)?.currency ?? null,
        timeWeightedReturnPct: null,
    };
    ADDITIVE.forEach((key) => { out[key] = field(key); });
    // The capital a return is measured on adds like the rest — but only when
    // every part has one, or the midpoint guess takes over for all of them.
    if (present.every((p) => p.capital !== null && p.capital !== undefined)) {
        out.capital = field('capital');
    }
    return out;
}

/** Accounts whose series is our own record, begun the day they were first
 *  counted — see `viewChange`. Their breakdown runs from inception rather than
 *  over a broker's statement period. */
const RECORDED_ONLY = new Set(['crypto', 'nft', 'private']);

export const isOwnBook = (account) => RECORDED_ONLY.has(account.source);

/**
 * The crypto P/L breakdown is held back while it is known to be wrong.
 *
 * Alchemy does not report ETH moved by a contract on Arbitrum or Base — ETH
 * from a router sale, or bridged in from mainnet — so the ledger never sees
 * ~100 ETH on the Arbitrum wallet arrive. The sales that produced it read as
 * withdrawals, net deposits come out too low, and net P/L ~$270k too high
 * (measured 2026-10-01). The VALUE is live from the chain and right; only the
 * breakdown waits on a second source for those transfers. Flip this once it is
 * in place.
 */
export const CRYPTO_PNL_WIP = true;

export const isWip = (account) => CRYPTO_PNL_WIP && account.source === 'crypto';

/**
 * An account's breakdown windows, as `windowPerformance` reads them.
 *
 * A wallet or the private book has ONE breakdown, since inception, and it is
 * offered for the All window only. It is never folded into a broker's
 * `period`: that is the Flex statement's year, and summing it with five years
 * of a wallet under the widest date range would describe neither.
 */
export function windowsFor(account) {
    if (!isOwnBook(account)) return account.performance;
    if (isWip(account)) return { period: null, monthToDate: null, yearToDate: null };
    return { period: account.performance?.inception ?? null, monthToDate: null, yearToDate: null };
}

/**
 * The accounts as the summary cards show them: every crypto wallet folded into
 * one Crypto card, where the wallets themselves would be eight cards saying
 * one thing. The wallets are still the accounts everywhere else.
 */
export function groupCrypto(accounts, label) {
    const wallets = accounts.filter((a) => a.source === 'crypto');
    if (wallets.length < 2) return accounts;
    const merged = {
        source: 'crypto',
        accountId: 'crypto',
        accountLabel: label,
        currency: 'USD',
        totalValue: totalValue(wallets),
        cash: null,
        asOf: wallets.map((a) => a.asOf).filter(Boolean).sort().at(-1) ?? null,
        navHistory: sumNavSeries(wallets.map((a) => a.navHistory)),
        performance: {
            period: null,
            monthToDate: null,
            yearToDate: null,
            inception: combinePerformance(wallets.map((a) => a.performance?.inception)),
        },
    };
    const first = accounts.indexOf(wallets[0]);
    const rest = accounts.filter((a) => a.source !== 'crypto');
    return [...rest.slice(0, first), merged, ...rest.slice(first)];
}

/**
 * Change in value across the accounts in view.
 *
 * The brokers' series are summed and measured as one, exactly as before. Each
 * crypto wallet — and the private investments — is measured on its OWN series
 * and added on, because its series
 * is one we began recording the day the wallet was first counted — not the day
 * the money arrived. Summed in with the brokers, it is worth zero before that
 * day, and every window reaching back past it would report the whole wallet as
 * a gain made inside the window.
 *
 * A wallet with a single recorded day has no change to report yet and is left
 * out, rather than counted as flat.
 */
export function viewChange(accounts, key) {
    const brokers = accounts.filter((a) => !RECORDED_ONLY.has(a.source));
    const wallets = accounts.filter((a) => RECORDED_ONLY.has(a.source));

    const parts = [
        brokers.length > 0 ? valueChange(sumNavSeries(brokers.map((a) => a.navHistory)), key) : null,
        ...wallets.map((a) => valueChange(a.navHistory, key)),
    ].filter(Boolean);
    if (parts.length === 0) return null;
    if (parts.length === 1) return parts[0];

    const from = parts.reduce((acc, p) => acc + p.startingValue, 0);
    const to = parts.reduce((acc, p) => acc + p.endingValue, 0);
    return {
        fromDate: parts.map((p) => p.fromDate).sort()[0],
        toDate: parts.map((p) => p.toDate).sort().at(-1),
        startingValue: from,
        endingValue: to,
        change: to - from,
        // The same floor `valueChange` applies: a percentage of nothing says
        // nothing.
        changePct: from > 0 && from >= Math.abs(to) / 100 ? ((to - from) / from) * 100 : null,
    };
}

/**
 * Net deposits over `timeframe` across `accounts`, from their statement
 * windows — what NavChart needs to say a change in value includes deposits.
 * Null for a window no statement covers.
 */
export function netDepositsIn(accounts, timeframe) {
    const windows = (key) => combinePerformance(accounts.map((a) => windowsFor(a)?.[key]));
    return windowPerformance(
        { period: windows('period'), monthToDate: windows('monthToDate'), yearToDate: windows('yearToDate') },
        timeframe,
    )?.netDeposits ?? null;
}

/** The first day any account of `source` in view was recorded, or null. */
export function recordedSince(accounts, source) {
    return accounts
        .filter((a) => a.source === source)
        .map((a) => a.navHistory?.[0]?.date)
        .filter(Boolean)
        .sort()[0] ?? null;
}

/** The first day any crypto wallet in view was recorded, or null. */
export function cryptoSince(accounts) {
    return accounts
        .filter((a) => a.source === 'crypto')
        .map((a) => a.navHistory?.[0]?.date)
        .filter(Boolean)
        .sort()[0] ?? null;
}
